import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { ChevronRight, Save, Eye, EyeOff, RotateCcw, Shield, ShieldCheck, Mail, Clock } from "lucide-react";

const DEFAULT_TEMPLATE = `CANDIDATE CONSENT AND PRIVACY NOTICE
Data Controller: [Company Name]
Privacy Contact: [privacy@companyemail.com]

1. INFORMATION WE COLLECT
I, [Full Name], consent to [Company Name] ("the Company") collecting and processing the following personal information I provide through this application:
  • Identity and contact details: name, email address, phone number, and mailing address
  • Professional history: resume, work experience, skills, qualifications, and certifications
  • Location information
  • LinkedIn profile URL and any other details I voluntarily include

2. HOW MY INFORMATION WILL BE USED
The Company will use my personal information solely for:
  • Evaluating my candidacy for the role I am currently applying for
  • Considering my profile for future job openings at [Company Name] that may be a good fit
  • Communicating with me regarding this or future opportunities within the retention period
  • Conducting interviews, assessments, and background verification where required by law

3. AUTOMATED PROCESSING AND AI SCORING
I understand that my application and resume will be analyzed by automated systems, including artificial intelligence, to evaluate how my qualifications match the job requirements. This automated scoring assists — but does not replace — human decision-making by the hiring team. I have the right to request a human review of any automated assessment by contacting [privacy@companyemail.com].

4. INTERVIEW SESSION RECORDING AND MONITORING
I understand that my interview session(s) conducted through the Company's platform may be recorded (audio and video) and monitored, including by automated systems, for the purpose of evaluating my candidacy. Recordings are retained for a limited period and are accessible only to authorised members of the hiring team. I have the right to request information about how these recordings are stored and processed by contacting [privacy@companyemail.com].

5. DATA RETENTION
The Company will retain my personal information for a maximum of 12 months from the date of this consent. After this period, my data will be securely and permanently deleted unless I provide renewed consent or a legal obligation requires its continued retention.

6. MY PRIVACY RIGHTS
At any time I may, without penalty to my current or future applications:
  • Withdraw this consent
  • Request access to the personal information the Company holds about me
  • Request correction of any inaccurate or incomplete information
  • Request permanent deletion of my personal information
  • Request a portable copy of my personal information

To exercise any of these rights, I may contact [privacy@companyemail.com]. The Company will acknowledge my request within 10 business days and respond fully within 30 calendar days.

7. DATA SECURITY AND THIRD PARTIES
The Company applies reasonable technical and organizational safeguards to protect my personal information. My data may be shared with third-party service providers (such as applicant tracking, cloud storage, and interview platform providers) who are contractually bound to process it securely and exclusively for recruitment purposes. My personal information will never be sold or shared with any unaffiliated third party for commercial purposes.

8. APPLICABLE LAW
This consent is governed by applicable privacy legislation, including the Personal Information Protection and Electronic Documents Act (PIPEDA), Quebec Law 25 (for Quebec residents), and applicable US state privacy laws including the California Consumer Privacy Act (CCPA) where applicable.

9. CONFIRMATION
I confirm that:
  • I am providing this consent freely and voluntarily
  • I have read and understood this notice in full
  • All information I have provided in this application is accurate and complete to the best of my knowledge
  • I am 18 years of age or older, or have reached the age of majority in my jurisdiction`;

export default function CompanyConsentSettings() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.role === "company_employee") {
      toast.error("Only company admins can manage the consent template");
      navigate("/company/roles", { replace: true });
    }
  }, [user, navigate]);

  const [body, setBody] = useState("");
  const [savedBody, setSavedBody] = useState("");
  const [privacyEmail, setPrivacyEmail] = useState("");
  const [savedPrivacyEmail, setSavedPrivacyEmail] = useState("");
  const [retentionMonths, setRetentionMonths] = useState(12);
  const [savedRetentionMonths, setSavedRetentionMonths] = useState(12);
  const [userEdited, setUserEdited] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    api.get("/company/consent-template")
      .then(({ data }) => {
        setBody(data.body);
        setSavedBody(data.body);
        setPrivacyEmail(data.privacy_email || "");
        setSavedPrivacyEmail(data.privacy_email || "");
        setRetentionMonths(data.retention_months || 12);
        setSavedRetentionMonths(data.retention_months || 12);
        setUserEdited(data.user_edited || false);
        setUpdatedAt(data.updated_at || null);
      })
      .catch(() => {
        setBody(DEFAULT_TEMPLATE);
        setSavedBody(DEFAULT_TEMPLATE);
      })
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!body.trim()) { toast.error("Template cannot be empty"); return; }
    setSaving(true);
    try {
      const payload = { body, retention_months: retentionMonths };
      if (privacyEmail.trim()) payload.privacy_email = privacyEmail.trim();
      const { data } = await api.put("/company/consent-template", payload);
      setSavedBody(body);
      setSavedPrivacyEmail(privacyEmail);
      setSavedRetentionMonths(retentionMonths);
      setUserEdited(true);
      setUpdatedAt(data?.updated_at || new Date().toISOString());
      toast.success("Consent template saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save template");
    } finally { setSaving(false); }
  };

  const reset = () => {
    setBody(DEFAULT_TEMPLATE);
    setPrivacyEmail(savedPrivacyEmail);
  };

  const isDirty = body !== savedBody || privacyEmail !== savedPrivacyEmail || retentionMonths !== savedRetentionMonths;

  const effectiveCompanyName = user?.name || "Acme Corp";
  const effectivePrivacyEmail = privacyEmail.trim() || "privacy@acmecorp.com";
  const previewText = body
    .replace(/\[Full Name\]/g, "Jane Smith")
    .replace(/\[Company Name\]/g, effectiveCompanyName)
    .replace(/\[privacy@companyemail\.com\]/g, effectivePrivacyEmail);

  if (loading) return <div className="p-12 font-mono text-sm text-muted-foreground">Loading…</div>;

  return (
    <main className="max-w-3xl mx-auto px-6 md:px-12 py-10">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground mb-6">
        <Link to="/company/roles" className="hover:text-foreground">ROLES</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-foreground">CONSENT SETTINGS</span>
      </div>

      <div className="rounded-xl border border-border bg-white">
        {/* Header */}
        <div className="px-8 pt-7 pb-5 border-b border-border flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground">COMPANY-WIDE SETTING</div>
              {!isDirty && (
                userEdited ? (
                  <span className="flex items-center gap-1 font-mono text-[9px] px-1.5 py-0.5 bg-[#0025D8]/10 text-[#0025D8] border border-[#0025D8]/30">
                    <ShieldCheck className="w-2.5 h-2.5" />CUSTOM
                  </span>
                ) : (
                  <span className="flex items-center gap-1 font-mono text-[9px] px-1.5 py-0.5 bg-secondary text-muted-foreground rounded-xl border border-border">
                    <Shield className="w-2.5 h-2.5" />DEFAULT
                  </span>
                )
              )}
            </div>
            <h1 className="font-display font-bold text-2xl tracking-tight text-[#0F172A]">Consent Template</h1>
            {!isDirty && updatedAt && (
              <div className="font-mono text-[9px] text-muted-foreground mt-0.5">
                {userEdited ? "Last saved" : "Last updated"}: {new Date(updatedAt).toLocaleString()}
              </div>
            )}
            <p className="text-sm text-muted-foreground mt-1.5 max-w-lg">
              This template is shown to candidates on your apply links when consent is enabled for a role.{" "}
              <code className="font-mono text-[11px] bg-secondary px-1">[Full Name]</code> and{" "}
              <code className="font-mono text-[11px] bg-secondary px-1">[Company Name]</code> are auto-filled per candidate.
              Enter your privacy contact email below — it replaces all{" "}
              <code className="font-mono text-[11px] bg-secondary px-1">[privacy@companyemail.com]</code>{" "}
              placeholders automatically.
            </p>
          </div>
        </div>

        <div className="px-8 py-7 space-y-5">

          {/* Privacy contact email — single input, replaces all occurrences */}
          <div>
            <label className="font-mono text-[10px] tracking-[0.15em] text-muted-foreground block mb-1.5">
              PRIVACY CONTACT EMAIL
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
              <input
                type="email"
                value={privacyEmail}
                onChange={(e) => setPrivacyEmail(e.target.value)}
                placeholder="privacy@yourcompany.com"
                className="w-full rounded-xl border border-border pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#0025D8] focus:ring-1 focus:ring-[#0025D8]/20 transition-all"
              />
            </div>
            <p className="font-mono text-[9px] text-muted-foreground mt-1">
              Automatically replaces all <code>[privacy@companyemail.com]</code> placeholders in the template below.
            </p>
          </div>

          {/* Retention period */}
          <div>
            <label className="font-mono text-[10px] tracking-[0.15em] text-muted-foreground block mb-1.5">
              DATA RETENTION PERIOD
            </label>
            <div className="relative flex items-center gap-2">
              <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
              <input
                type="number"
                min={1}
                max={120}
                value={retentionMonths}
                onChange={(e) => setRetentionMonths(Math.max(1, Math.min(120, parseInt(e.target.value) || 12)))}
                className="w-28 rounded-xl border border-border pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#0025D8] focus:ring-1 focus:ring-[#0025D8]/20 transition-all"
              />
              <span className="text-sm text-muted-foreground">months</span>
            </div>
            <p className="font-mono text-[9px] text-muted-foreground mt-1">
              Consent records are automatically expired and PII wiped after this period. Must be between 1–120 months.
            </p>
          </div>

          <div className="border-t border-border" />

          {/* Toggle preview */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setPreview((p) => !p)}
              className="flex items-center gap-2 font-mono text-[10px] tracking-[0.15em] text-muted-foreground hover:text-foreground transition-colors rounded-xl border border-border px-3 py-1.5"
            >
              {preview ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              {preview ? "EDIT" : "PREVIEW"}
            </button>
            <button
              type="button"
              onClick={reset}
              className="flex items-center gap-2 font-mono text-[10px] tracking-[0.15em] text-muted-foreground hover:text-foreground transition-colors rounded-xl border border-border px-3 py-1.5"
            >
              <RotateCcw className="w-3 h-3" />
              RESET TO DEFAULT
            </button>
          </div>

          {preview ? (
            <div className="rounded-xl border border-border bg-[#FAFAFA] p-5">
              <div className="font-mono text-[9px] tracking-[0.2em] text-muted-foreground mb-3">PREVIEW — CANDIDATE VIEW</div>
              <div className="text-sm text-foreground whitespace-pre-wrap leading-relaxed font-mono">{previewText}</div>
            </div>
          ) : (
            <div>
              <label className="font-mono text-[10px] tracking-[0.15em] text-muted-foreground block mb-2">
                TEMPLATE BODY
              </label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={14}
                className="w-full rounded-xl border border-border p-4 text-sm font-mono leading-relaxed focus:outline-none focus:border-[#0025D8] focus:ring-1 focus:ring-[#0025D8]/20 resize-y transition-all"
              />
            </div>
          )}

          {/* Save */}
          <div className="flex items-center justify-between pt-2">
            {isDirty ? (
              <span className="font-mono text-[10px] text-[#F59E0B] tracking-[0.1em]">UNSAVED CHANGES</span>
            ) : (
              <span className="font-mono text-[10px] text-[#10B981] tracking-[0.1em]">SAVED</span>
            )}
            <button
              onClick={save}
              disabled={saving || !isDirty}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#0025D8] text-white font-bold text-sm hover:bg-[#001CB3] shadow-lg shadow-[#0025D8]/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {saving ? "Saving…" : "Save Template"}
            </button>
          </div>
        </div>
      </div>

      {/* Info box */}
      <div className="mt-6 rounded-xl border border-border bg-white p-5">
        <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground mb-3">HOW IT WORKS</div>
        <div className="space-y-2 text-sm text-muted-foreground">
          <p>1. Enter your privacy contact email above — it replaces every <code className="font-mono text-[11px]">[privacy@companyemail.com]</code> in the template automatically.</p>
          <p>2. Enable the consent form per role in the <strong className="text-foreground">Interview Setup</strong> tab of each role dashboard.</p>
          <p>3. Configure which questions to ask (work authorization, notice period, start date).</p>
          <p>4. Candidates will see a Step 2 on the apply link with this template and must agree before submitting.</p>
          <p>5. The exact template text at the time of consent is stored with each application as a legal record.</p>
        </div>
      </div>
    </main>
  );
}
